/**
 * What each `ThemeSpec` choice means in numbers. The one source for these values: the theme
 * writer turns them into CSS variables, and the Figma sync and the Foundations stories read them
 * from here, so a value changes in one place.
 */
import type {
  CONTROL_SHAPES,
  DENSITIES,
  DISPLAY_TRACKINGS,
  GLASS_STRENGTHS,
  GLOW_INTENSITIES,
  MOTION_PRESETS,
} from "@loot-os/api-contract";

/** The radius the tier values below are drawn at; a tenant's `radius` scales them. */
export const BASE_RADIUS_PX = 8;

/** Radius tiers in px at the base radius. `pill` never scales. */
export const RADIUS_TIERS = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 28,
} as const;
export type RadiusTier = keyof typeof RADIUS_TIERS | "pill";

/** The radius every control (button, chip, input, segmented control, nav link) takes. */
export const CONTROL_RADIUS: Record<(typeof CONTROL_SHAPES)[number], string> = {
  pill: "9999px",
  rounded: "var(--radius-sm)",
};

/** Control heights in px at default density. */
export const CONTROL_HEIGHTS = { sm: 32, md: 40, lg: 48 } as const;

/** Multiplies control heights and component padding. Gutters and content sizes don't scale. */
export const DENSITY_SCALE: Record<(typeof DENSITIES)[number], number> = {
  compact: 0.875,
  default: 1,
  roomy: 1.125,
};

/** Multiplies each display role's letter spacing (negative: tighter). */
export const DISPLAY_TRACKING_SCALE: Record<(typeof DISPLAY_TRACKINGS)[number], number> = {
  tight: 1,
  normal: 0,
};

/** Backdrop blur in px per glass strength. 0 renders glass roles as solid raised surfaces. */
export const GLASS_BLUR_PX: Record<(typeof GLASS_STRENGTHS)[number], number> = {
  0: 0,
  1: 12,
  2: 20,
  3: 28,
};

/** How strongly glows show, as the glow color's share in its shadow. */
export const GLOW_ALPHA: Record<(typeof GLOW_INTENSITIES)[number], string> = {
  0: "0%",
  1: "24%",
  2: "36%",
  3: "50%",
};

interface MotionPreset {
  durations: { fast: number; base: number; slow: number };
  /**
   * `smooth` is a spring's settle drawn as `linear()`: for motion driven by script (the Web
   * Animations API) that has to reverse from wherever it is, like a box card's top card.
   */
  easings: { standard: string; enter: string; exit: string; smooth: string };
  /** One run of an icon's animation, in ms (`@loot-os/icons` draws them for 1200). */
  iconAnimation: number;
}

/** A critically damped spring's settle, sampled; every preset's `smooth`. */
const SPRING_SMOOTH =
  "linear(0, 0.005 1.4%, 0.019 2.8%, 0.045 4.4%, 0.085 6.4%, 0.16 9.4%, 0.358 16.7%, 0.453 20.3%, 0.539 23.9%, 0.615 27.5%, 0.681 31.1%, 0.738 34.7%, 0.786 38.3%, 0.829 42.2%, 0.87 46.9%, 0.906 52.2%, 0.934 57.8%, 0.956 63.9%, 0.973 70.8%, 0.985 78.9%, 0.994 88.3%, 1)";

/** Durations in ms and easing curves per preset. Openers and stages read the same preset. */
export const MOTION: Record<(typeof MOTION_PRESETS)[number], MotionPreset> = {
  calm: {
    iconAnimation: 1400,
    durations: { fast: 150, base: 260, slow: 420 },
    easings: {
      standard: "cubic-bezier(0.2, 0, 0, 1)",
      enter: "cubic-bezier(0, 0, 0, 1)",
      exit: "cubic-bezier(0.3, 0, 1, 1)",
      smooth: SPRING_SMOOTH,
    },
  },
  standard: {
    iconAnimation: 1200,
    durations: { fast: 120, base: 200, slow: 320 },
    easings: {
      standard: "cubic-bezier(0.2, 0, 0, 1)",
      enter: "cubic-bezier(0.05, 0.7, 0.1, 1)",
      exit: "cubic-bezier(0.3, 0, 0.8, 0.15)",
      smooth: SPRING_SMOOTH,
    },
  },
  lively: {
    iconAnimation: 1000,
    durations: { fast: 100, base: 180, slow: 280 },
    easings: {
      standard: "cubic-bezier(0.3, 0, 0, 1.15)",
      enter: "cubic-bezier(0.34, 1.4, 0.64, 1)",
      exit: "cubic-bezier(0.4, 0, 1, 1)",
      smooth: SPRING_SMOOTH,
    },
  },
};

/** Status colors the shadcn contract has no variable for: the default when a theme sets none. */
export const STATUS_DEFAULTS = {
  "status-success": "#3bd99a",
  "status-warning": "#ffc74d",
} as const;

/** The chase tier's default when a theme sets no `rarity-chase`. */
export const CHASE_DEFAULT = "#eebf19";

interface TypeRole {
  /** px */
  size: number;
  /** px */
  lineHeight: number;
  /** em, before the display tracking scale (display roles) */
  tracking: number;
  /**
   * A number, "display" for the theme's display weight, or "strong" for its text weight (the
   * emphasized body roles, `type.textWeight`).
   */
  weight: number | "display" | "strong";
  family: "body" | "display" | "mono";
  use: string;
}

/**
 * The type roles, named as the LootOS Figma file's text styles. Sizes are the same for
 * every tenant; a theme changes the families, the display weight, the display tracking and the
 * display case.
 */
export const TYPE_ROLES = {
  "display-hero": {
    size: 76,
    lineHeight: 78,
    tracking: -0.03,
    weight: "display",
    family: "display",
    use: "The one headline at the top of Home, desktop.",
  },
  "display-band": {
    size: 60,
    lineHeight: 60,
    tracking: -0.02,
    weight: "display",
    family: "display",
    use: "The closing band headline, desktop.",
  },
  "display-xl1": {
    size: 56,
    lineHeight: 60,
    tracking: -0.02,
    weight: "display",
    family: "display",
    use: "Hero headline on a stage. One per page.",
  },
  "display-xl2": {
    size: 40,
    lineHeight: 44,
    tracking: -0.015,
    weight: "display",
    family: "display",
    use: "Page titles.",
  },
  "display-xl3": {
    size: 32,
    lineHeight: 36,
    tracking: -0.01,
    weight: "display",
    family: "display",
    use: "Section titles.",
  },
  "display-xl4": {
    size: 24,
    lineHeight: 28,
    tracking: -0.005,
    weight: "display",
    family: "display",
    use: "Panel and card titles.",
  },
  "display-section": {
    size: 16,
    lineHeight: 21,
    tracking: 0.0125,
    weight: "display",
    family: "display",
    use: "A section's title among several on a page: Recent pulls, What's inside.",
  },
  "heading-h1": {
    size: 20,
    lineHeight: 26,
    tracking: -0.005,
    weight: 600,
    family: "body",
    use: "Modal titles, large panel headings.",
  },
  "heading-h2": {
    size: 17,
    lineHeight: 22,
    tracking: -0.003,
    weight: 600,
    family: "body",
    use: "Sub-sections; section titles on mobile.",
  },
  "heading-h3": {
    size: 15,
    lineHeight: 20,
    tracking: 0,
    weight: 600,
    family: "body",
    use: "Card titles, list-item titles, labels above components.",
  },
  "body-b1": {
    size: 14,
    lineHeight: 20,
    tracking: 0,
    weight: 400,
    family: "body",
    use: "Default reading text.",
  },
  "body-b1-strong": {
    size: 14,
    lineHeight: 20,
    tracking: 0,
    weight: "strong",
    family: "body",
    use: "Emphasized labels, large button labels, nav links.",
  },
  "body-b2": {
    size: 13,
    lineHeight: 18,
    tracking: 0,
    weight: 400,
    family: "body",
    use: "Secondary text, helper copy, toasts.",
  },
  "body-b2-strong": {
    size: 13,
    lineHeight: 18,
    tracking: 0,
    weight: "strong",
    family: "body",
    use: "Medium button labels, chips, usernames.",
  },
  "body-b3": {
    size: 12,
    lineHeight: 16,
    tracking: 0,
    weight: 400,
    family: "body",
    use: "Meta under cards, tab labels, captions.",
  },
  "body-b3-strong": {
    size: 12,
    lineHeight: 16,
    tracking: 0,
    weight: "strong",
    family: "body",
    use: "Emphasized meta.",
  },
  "caption-eyebrow": {
    size: 11,
    lineHeight: 14,
    tracking: 0.12,
    weight: 500,
    family: "display",
    use: "One small uppercase label above a title. At most one per block.",
  },
  "mono-price-l": {
    size: 20,
    lineHeight: 24,
    tracking: -0.02,
    weight: "strong",
    family: "mono",
    use: "Headline money and stats.",
  },
  "mono-price": {
    size: 14,
    lineHeight: 18,
    tracking: -0.01,
    weight: "strong",
    family: "mono",
    use: "Money in rows and pills.",
  },
  "mono-meta": {
    size: 11,
    lineHeight: 14,
    tracking: 0,
    weight: "strong",
    family: "mono",
    use: "Set codes, odds, small amounts: anything a collector compares.",
  },
} as const satisfies Record<string, TypeRole>;
export type TypeRoleName = keyof typeof TYPE_ROLES;
